import { useState } from "react";
import { MapPin, Clock, MessageCircle, Phone, Star, Utensils, Prayer } from "lucide-react";
import "./RestaurantCard.scss";
import { useNavigate } from "react-router-dom";

const RestaurantCard = ({ restaurant }) => {
  const [expanded, setExpanded] = useState(false);
  const navigate = useNavigate();

  const formatDate = (date) => {
    return new Date(date).toLocaleDateString("en-US", {
      day: "numeric",
      month: "short",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const openGoogleMaps = (e) => {
    e.stopPropagation();
    if (restaurant.googleMapsLink) {
      window.open(restaurant.googleMapsLink, "_blank");
    }
  };

  const handleCall = (e) => {
    e.stopPropagation();
    if (restaurant.contact?.phone) {
      window.location.href = `tel:${restaurant.contact.phone}`;
    }
  };

  const toggleDescription = () => {
    setExpanded(!expanded);
  };

  const getShortDescription = (text, wordLimit) => {
    if (!text) return '';
    const words = text.split(" ");
    return words.length > wordLimit
      ? words.slice(0, wordLimit).join(" ") + "..."
      : text;
  };

  const handleCardClick = (e) => {
    // Don't navigate if clicking on action buttons or description
    if (
      !e.target.closest('.restaurant-card__map-icon') &&
      !e.target.closest('.restaurant-card__call-button') &&
      !e.target.closest('.restaurant-card__description')
    ) {
      navigate(`/restaurant/${restaurant.restaurantID}`);
    }
  };

  const renderStars = (rating) => {
    if (!rating) return null;
    const fullStars = Math.floor(rating);
    const hasHalfStar = rating % 1 >= 0.5;
    const emptyStars = 5 - fullStars - (hasHalfStar ? 1 : 0);

    return (
      <div className="restaurant-card__rating">
        <div className="restaurant-card__stars">
          {[...Array(fullStars)].map((_, i) => (
            <Star key={i} size={14} fill="#fbbf24" color="#fbbf24" />
          ))}
          {hasHalfStar && <Star size={14} fill="#fbbf24" color="#fbbf24" style={{ clipPath: 'inset(0 50% 0 0)' }} />}
          {[...Array(emptyStars)].map((_, i) => (
            <Star key={i} size={14} color="#d1d5db" />
          ))}
        </div>
        <span className="restaurant-card__rating-value">{rating.toFixed(1)}</span>
        {restaurant.googleReviewCount > 0 && (
          <span className="restaurant-card__review-count">({restaurant.googleReviewCount})</span>
        )}
      </div>
    );
  };

  return (
    <div className="restaurant-card" onClick={handleCardClick}>
      {restaurant.premium && (
        <div className="restaurant-card__premium-badge">
          <Star size={14} fill="#fbbf24" color="#fbbf24" />
          Premium
        </div>
      )}

      <div className="restaurant-card__top-actions">
        {restaurant.verified && (
          <div className="restaurant-card__verified-badge">
            <svg viewBox="0 0 24 24" fill="none" className="verified-icon">
              <path d="M9 12L11 14L15 10M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12Z"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            Verified
          </div>
        )}

        <div className="restaurant-card__actions">
          {restaurant.contact?.phone && (
            <button className="restaurant-card__call-button" onClick={handleCall} title="Call">
              <Phone size={16} />
            </button>
          )}
          <MapPin size={18} className="restaurant-card__map-icon" onClick={openGoogleMaps} title="View on Map" />
        </div>
      </div>

      <div className="restaurant-card__content">
        <div className="restaurant-card__header">
          <div className="restaurant-card__icon">
            <Utensils size={18} />
          </div>
          <h2 className="restaurant-card__name">{restaurant.name}</h2>
        </div>
        
        <div className="restaurant-card__iftar-badge">
          <span className="iftar-label">🌙 Iftar & Breaking Fast</span>
        </div>
        
        <div className="restaurant-card__facilities">
          {restaurant.namazFacility && (
            <div className="restaurant-card__facility-badge">
              <span>🤲</span>
              <span>Namaz</span>
              <div className="tooltip">Namaz Facility Available</div>
            </div>
          )}
          {restaurant.womenFacility && (
            <div className="restaurant-card__facility-badge women">
              <span>🧕</span>
              <span>Women</span>
              <div className="tooltip">Facility for Women</div>
            </div>
          )}
        </div>
        
        <p className="restaurant-card__location">{restaurant.district}, {restaurant.state}</p>

        {restaurant.googleRating && renderStars(restaurant.googleRating)}

        {restaurant.distance && <p className="restaurant-card__distance">📍 {restaurant.distance.toFixed(2)} km away</p>}

        {restaurant.description && (
          <div className="restaurant-card__description-wrapper">
            <p
              className="restaurant-card__description"
              onClick={toggleDescription}
              title="Click to expand"
            >
              {expanded ? restaurant.description : getShortDescription(restaurant.description, 30)}
            </p>
            {restaurant.description && restaurant.description.split(' ').length > 30 && (
              <span className="restaurant-card__expand-hint">
                {expanded ? 'Show less' : 'Read more'}
              </span>
            )}
          </div>
        )}

        <div className="restaurant-card__footer">
          <div className="restaurant-card__footer-left">
            <span className="restaurant-card__updated">
              <Clock size={16} />
              {formatDate(restaurant.date)}
            </span>
          </div>

          <div className="restaurant-card__comments" title={`${restaurant.comments?.length || 0} Comments`}>
            <MessageCircle size={16} />
            <span className="comment-count">{restaurant.comments?.length || 0}</span>
            <div className="tooltip">
              {restaurant.comments?.length || 0} Comments
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RestaurantCard;
